import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import api from "../../../api/client";
import logger from "../../../utils/logger";
import { ArrowRight, ArrowLeft, Headphones, Smartphone, Monitor, Zap, Footprints, Shirt, User, Code2, Gamepad2, Home, ShoppingBag } from "lucide-react";
import "./ShopByCategory.css";

const DEFAULT_CATEGORIES = [
    { 
      id: 19,
      slug: "accessories",
      name_en: "Accessories", 
      name_ar: "إكسسوارات",
      image: "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=400&q=80",
      color: "#E8F4FD",
      Icon: Headphones
    },
    { 
      id: 18,
      slug: "phones",
      name_en: "Phones & Telecom", 
      name_ar: "الهواتف والاتصالات",
      image: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&q=80",
      color: "#FFF3E0",
      Icon: Smartphone
    },
    { 
      id: 17,
      slug: "computer",
      name_en: "Computer & Office", 
      name_ar: "الكمبيوتر والمكتب",
      image: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&q=80",
      color: "#E8F5E9",
      Icon: Monitor
    },
    { 
      id: 16,
      slug: "electronics",
      name_en: "Electronics", 
      name_ar: "إلكترونيات",
      image: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80",
      color: "#F3E5F5",
      Icon: Zap
    },
    { 
      id: 15,
      slug: "shoes",
      name_en: "Shoes", 
      name_ar: "أحذية",
      image: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80",
      color: "#FFEBEE",
      Icon: Footprints
    },
    { 
      id: 14,
      slug: "men",
      name_en: "Men's Clothing", 
      name_ar: "ملابس رجالية",
      image: "https://images.unsplash.com/photo-1490578474895-699cd4e2cf59?w=400&q=80",
      color: "#E3F2FD",
      Icon: User
    },
    { 
      id: 13,
      slug: "women",
      name_en: "Women's Clothing", 
      name_ar: "ملابس نسائية",
      image: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&q=80",
      color: "#FCE4EC",
      Icon: Shirt
    },
    { 
      id: 11,
      slug: "programming",
      name_en: "Programming", 
      name_ar: "برمجة",
      image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&q=80",
      color: "#E0F7FA",
      Icon: Code2
    },
    { 
      id: 10,
      slug: "digital",
      name_en: "Digital Products", 
      name_ar: "منتجات رقمية",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&q=80",
      color: "#FFF8E1",
      Icon: Gamepad2
    },
    { 
      id: 8,
      slug: "housewares",
      name_en: "Housewares", 
      name_ar: "أدوات منزلية",
      image: "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=400&q=80",
      color: "#F1F8E9",
      Icon: Home
    },
    { 
      id: 7,
      slug: "clothing",
      name_en: "Clothing", 
      name_ar: "ملابس",
      image: "https://images.unsplash.com/photo-1445205170230-053b83016050?w=400&q=80",
      color: "#E8EAF6",
      Icon: ShoppingBag
    }
];

function getIconForCategory(categoryId) {
  const iconMap = {
    19: Headphones, 18: Smartphone, 17: Monitor, 16: Zap, 15: Footprints,
    14: User, 13: Shirt, 11: Code2, 10: Gamepad2, 8: Home, 7: ShoppingBag,
  };
  return iconMap[categoryId] || ShoppingBag;
}

function getCategoryColor(categoryId) {
  const colorMap = {
    19: "#E8F4FD", 18: "#FFF3E0", 17: "#E8F5E9", 16: "#F3E5F5", 15: "#FFEBEE",
    14: "#E3F2FD", 13: "#FCE4EC", 11: "#E0F7FA", 10: "#FFF8E1", 8: "#F1F8E9", 7: "#E8EAF6"
  };
  return colorMap[categoryId] || "#F5F5F5";
}

function getCategoryImage(slug) {
  const imageMap = {
    accessories: "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=400&q=80",
    phones: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=400&q=80",
    computer: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=400&q=80",
    electronics: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80",
    shoes: "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&q=80",
    men: "https://images.unsplash.com/photo-1490578474895-699cd4e2cf59?w=400&q=80",
    women: "https://images.unsplash.com/photo-1483985988355-763728e1935b?w=400&q=80",
    programming: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400&q=80",
    digital: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&q=80",
    housewares: "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?w=400&q=80",
    clothing: "https://images.unsplash.com/photo-1445205170230-053b83016050?w=400&q=80",
  };
  return imageMap[slug] || "https://images.unsplash.com/photo-1488459716781-6818f2af1c3e?w=400&q=80";
}

export default function ShopByCategory() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function loadCategories() {
      try {
        const res = await api.apiGet("categories");
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.categories) ? res.categories : [];
        const categoriesWithMetadata = list
          .filter(cat => cat.is_active === 1 || cat.is_active === true)
          .map(cat => ({
            id: cat.id,
            slug: cat.slug || `cat-${cat.id}`,
            name_en: cat.name_en || cat.name || "",
            name_ar: cat.name_ar || "",
            image: getCategoryImage(cat.slug || `cat-${cat.id}`),
            color: getCategoryColor(cat.id),
            Icon: getIconForCategory(cat.id),
          }))
          .sort((a, b) => (a.name_en || "").localeCompare(b.name_en || ""));

        if (categoriesWithMetadata.length > 0) {
          setCategories(categoriesWithMetadata);
        }
      } catch (e) {
        logger.warn("Error loading categories from API, using defaults", e);
      }
    }

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchCategoryCounts() {
      try {
        setLoading(true);
        const res = await api.apiGet("products");
        if (cancelled) return;
        const products = Array.isArray(res?.products) ? res.products : [];

        const counts = {};
        products.forEach(product => {
          const categoryId = String(product.category_id);
          if (categoryId) {
            counts[categoryId] = (counts[categoryId] || 0) + 1;
          }
        });

        setCategoryCounts(counts);
      } catch (e) {
        logger.error("Error fetching category counts", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCategoryCounts();
    return () => {
      cancelled = true;
    };
  }, []);

  // -------------------------------------------------------
  // عند الضغط على فئة — ننقل المستخدم لصفحة CategoryPage
  // نمرر categoryId كـ String لأن الـ API يستخدم String
  // -------------------------------------------------------
  const handleCategoryClick = (category) => {
    navigate(`/category/${category.slug}`, {
      state: { 
        categoryId: String(category.id),  // "7" وليس 7 — مهم جداً للمقارنة في CategoryPage
        categoryName: isAr ? category.name_ar : category.name_en 
      }
    });
  };

  return (
    <section className="shop-by-category-section" dir={isAr ? "rtl" : "ltr"}>
      <div className="category-header">
        <h2 className="category-main-title">
          {isAr ? "تسوق حسب الفئة" : "Shop by Category"}
        </h2>
        <p className="category-subtitle">
          {isAr 
            ? "اكتشف مجموعتنا المتنوعة من المنتجات عبر مختلف الفئات"
            : "Explore our diverse collection across various categories"
          }
        </p>
      </div>

      <div className="category-grid-modern">
        {categories.map((category, index) => {
          // نبحث بـ String لأن مفاتيح counts هي Strings
          const productCount = categoryCounts[String(category.id)] || 0;
          const IconComponent = category.Icon;
          
          return (
            <div
              key={category.id}
              className="category-card-modern"
              style={{ 
                backgroundColor: category.color,
                animationDelay: `${index * 0.05}s`
              }}
              onClick={() => handleCategoryClick(category)}
            >
              <div className="category-image-container">
                <img 
                  src={category.image} 
                  alt={isAr ? category.name_ar : category.name_en}
                  className="category-image-modern"
                  loading="lazy"
                />
                <div className="category-overlay">
                  <div className="category-icon-lucide">
                    <IconComponent size={24} strokeWidth={1.5} />
                  </div>
                </div>
              </div>
              
              <div className="category-info">
                <h3 className="category-name-modern">
                  {isAr ? category.name_ar : category.name_en}
                </h3>
                <div className="category-meta">
                  <span className="category-count-modern">
                    {loading ? (
                      <span className="count-skeleton">...</span>
                    ) : (
                      `${productCount} ${isAr ? "منتج" : "Products"}`
                    )}
                  </span>
                  <span className="category-arrow">
                    {isAr ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}